import * as React from 'react';
import {
    Alert,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Snackbar,
    TextField,
    Card,
    CardContent,
    Modal,
    Box,
    IconButton,
    Chip,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@mui/material";
import {Lock} from "@mui/icons-material";
import {Save, Close, Edit, PlayArrow, Pause, Stop, CheckCircle} from "@mui/icons-material";
import {useContext, useEffect, useState} from "react";
import {DatePicker} from "@mui/x-date-pickers";
import {
    CURRENCY,
    UPDATE_PROJECT_URL,
    PROJECT_STATUS,
    PROJECT_STATUS_LABELS,
    PROJECT_STATUS_COLORS,
    PROJECT_STATUS_DESCRIPTIONS,
    EDITABLE_PROJECT_STATUSES
} from "../../../utils/constant";
import {validateProjectForm, ProjectFormErrors} from "../../../utils/projectValidation";
import dayjs from "dayjs";
import 'dayjs/locale/fr';
import useAxios from "../../../hooks/useAxios";
import {CommonContext} from "../../../contexts/CommonContext";
import {AdminProjectContext} from "../../../contexts/AdminProjectContext";
import {Project} from "../../../Models/Project";
import "../../../styles/NewProjectForm.css";
import logger from "../../../utils/logger";

// Set the global locale to French
dayjs.locale('fr');

interface AdminProjectEditingModalProps {
    openModal: boolean;
    setOpenModal: (open: boolean) => void;
    openedProject: Project;
    setOpenedProject: (project: Project) => void;
}

export default function AdminProjectEditingModal({
                                                     openModal,
                                                     setOpenModal,
                                                     openedProject,
                                                     setOpenedProject
                                                 }: AdminProjectEditingModalProps) {
    const {setNeedToRefreshData} = useContext(CommonContext);
    const {refreshProjects} = useContext(AdminProjectContext);
    const {putWithAxios} = useAxios();

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [contribution, setContribution] = useState<number>(0);
    const [currency, setCurrency] = useState<string>(CURRENCY[0].label);
    const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
    const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
    const [status, setStatus] = useState<string>(PROJECT_STATUS.DRAFT);

    // Nouveaux champs
    const [dueDay, setDueDay] = useState<number>(1);
    const [withdrawalDay, setWithdrawalDay] = useState<number>(1);
    const [penaltyAmountPerDay, setPenaltyAmountPerDay] = useState<number>(0);
    const [cashFundAmount, setCashFundAmount] = useState<number>(0);

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [formErrors, setFormErrors] = useState<ProjectFormErrors>({
        name: '',
        description: '',
        contribution: '',
        startDate: '',
        dueDay: '',
        withdrawalDay: '',
        penaltyAmountPerDay: '',
        cashFundAmount: '',
    });

    // État pour la confirmation de fermeture
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [initialValues, setInitialValues] = useState<{
        name: string;
        description: string;
        contribution: number;
        currency: string;
        startDate: dayjs.Dayjs | null;
        status: string;
        dueDay: number;
        withdrawalDay: number;
        penaltyAmountPerDay: number;
        cashFundAmount: number;
    }>({
        name: '',
        description: '',
        contribution: 0,
        currency: CURRENCY[0].label,
        startDate: null,
        status: PROJECT_STATUS.DRAFT,
        dueDay: 1,
        withdrawalDay: 1,
        penaltyAmountPerDay: 0,
        cashFundAmount: 0
    });

    // Initialize form with project data when modal opens
    useEffect(() => {
        if (openedProject) {
            logger.debug('📊 Loading project data:', {
                name: openedProject.name,
                description: openedProject.description,
                dueDay: openedProject.dueDay,
                withdrawalDay: openedProject.withdrawalDay,
                penaltyAmountPerDay: openedProject.penaltyAmountPerDay,
                cashFundAmount: openedProject.cashFundAmount,
            });

            setName(openedProject.name || '');
            setDescription(openedProject.description || '');
            setContribution(openedProject.contribution || 0);
            setCurrency(openedProject.currency || CURRENCY[0].label);
            setStartDate(openedProject.startDate ? dayjs(openedProject.startDate) : null);
            setEndDate(openedProject.endDate ? dayjs(openedProject.endDate) : null);
            setStatus(openedProject.status || PROJECT_STATUS.DRAFT);

            // Nouveaux champs
            setDueDay(openedProject.dueDay || 1);
            setWithdrawalDay(openedProject.withdrawalDay || 1);
            setPenaltyAmountPerDay(openedProject.penaltyAmountPerDay || 0);
            setCashFundAmount(openedProject.cashFundAmount || 0);

            // Sauvegarder les valeurs initiales pour détecter les changements
            setInitialValues({
                name: openedProject.name || '',
                description: openedProject.description || '',
                contribution: openedProject.contribution || 0,
                currency: openedProject.currency || CURRENCY[0].label,
                startDate: openedProject.startDate ? dayjs(openedProject.startDate) : null,
                status: openedProject.status || PROJECT_STATUS.DRAFT,
                dueDay: openedProject.dueDay || 1,
                withdrawalDay: openedProject.withdrawalDay || 1,
                penaltyAmountPerDay: openedProject.penaltyAmountPerDay || 0,
                cashFundAmount: openedProject.cashFundAmount || 0
            });

            setGlobalError('');
            setGlobalInfo('');
            setShowConfirmDialog(false);
        }
    }, [openedProject]);

    // Vérifie si les champs financiers peuvent être modifiés (seulement en DRAFT)
    const canEditFinancialFields = () => {
        return EDITABLE_PROJECT_STATUSES.includes(status as any);
    };

    // Vérifie si le projet n'est pas archivé (permet l'édition du nom/description)
    const canEditBasicFields = () => {
        return status !== PROJECT_STATUS.ARCHIVED;
    };

    // Message tooltip pour les champs verrouillés
    const lockedFieldTooltip = "Ce champ ne peut plus être modifié car le projet a démarré. Les paramètres financiers sont figés pour garantir l'intégrité des calculs.";

    const getStatusIcon = (statusValue: string) => {
        switch (statusValue) {
            case PROJECT_STATUS.STARTED:
                return <PlayArrow fontSize="small" />;
            case PROJECT_STATUS.ARCHIVED:
                return <CheckCircle fontSize="small" />;
            case PROJECT_STATUS.DRAFT:
            default:
                return <Edit fontSize="small" />;
        }
    };

    const validateForm = () => {
        // Utiliser la validation centralisée
        const errors = validateProjectForm({
            name,
            description,
            contribution,
            startDate,
            dueDay,
            withdrawalDay,
            cashFundAmount,
            penaltyAmountPerDay
        });

        setFormErrors(errors);
        return Object.values(errors).every((err) => err === '');
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setGlobalError('');

        try {
            const projectData = {
                name: name.trim(),
                description: description.trim(),
                contribution,
                currency,
                startDate: startDate?.toISOString(),
                status,
                dueDay,
                withdrawalDay,
                penaltyAmountPerDay,
                cashFundAmount,
            };

            const projectId = openedProject.uuid || openedProject._id;
            if (!projectId) {
                setGlobalError("Impossible de trouver l'identifiant du projet.");
                return;
            }

            const response = await putWithAxios(UPDATE_PROJECT_URL(projectId), projectData);

            if (response.success) {
                setGlobalInfo(response.message || 'Projet modifié avec succès');
                setNeedToRefreshData(true);

                // Rafraîchir la liste des projets pour obtenir les données complètes
                await refreshProjects();

                setTimeout(() => {
                    setOpenModal(false);
                }, 1500);
            } else {
                setGlobalError(response.message || "Erreur lors de la modification du projet");
            }
        } catch (error) {
            logger.error('Project update error:', error);
            setGlobalError(error instanceof Error ? error.message : "Erreur lors de la modification du projet");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusChange = (newStatus: string) => {
        setStatus(newStatus);
    };

    const handleCurrencyChange = (event: SelectChangeEvent) => {
        setCurrency(event.target.value);
    };

    // Détecter si des modifications ont été faites
    const hasUnsavedChanges = (): boolean => {
        const startDateChanged = startDate?.format('YYYY-MM-DD') !== initialValues.startDate?.format('YYYY-MM-DD');

        return (
            name !== initialValues.name ||
            description !== initialValues.description ||
            contribution !== initialValues.contribution ||
            currency !== initialValues.currency ||
            startDateChanged ||
            status !== initialValues.status ||
            dueDay !== initialValues.dueDay ||
            withdrawalDay !== initialValues.withdrawalDay ||
            penaltyAmountPerDay !== initialValues.penaltyAmountPerDay ||
            cashFundAmount !== initialValues.cashFundAmount
        );
    };

    // Fermeture forcée (sans vérification)
    const forceClose = () => {
        setShowConfirmDialog(false);
        setOpenModal(false);
        setGlobalError('');
        setGlobalInfo('');
    };

    // Fermeture avec vérification des changements
    const handleClose = () => {
        if (hasUnsavedChanges()) {
            setShowConfirmDialog(true);
        } else {
            forceClose();
        }
    };

    // Gestion du clic sur le backdrop du Modal
    const handleModalClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') {
            return; // Bloquer la fermeture par clic extérieur
        }
        handleClose();
    };

    const isFormValid =
        name.trim() !== '' &&
        description.trim() !== '' &&
        contribution > 0 &&
        startDate &&
        dueDay > 0 &&
        withdrawalDay > 0 &&
        penaltyAmountPerDay >= 0;

    return (
        <>
        <Modal
            open={openModal}
            onClose={handleModalClose}
            aria-labelledby="edit-project-modal"
            aria-describedby="edit-project-form"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' },
                maxWidth: '850px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: 24,
                overflow: 'hidden',
            }}>
                {/* ========== HEADER FIXE ========== */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2.5,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                    flexShrink: 0,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 45,
                            height: 45,
                            borderRadius: '12px',
                            background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        }}>
                            <Edit sx={{ color: 'white', fontSize: '1.3rem' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                                Modifier le Projet
                            </Typography>
                            <Chip
                                icon={getStatusIcon(status)}
                                label={PROJECT_STATUS_LABELS[status as keyof typeof PROJECT_STATUS_LABELS]}
                                sx={{
                                    backgroundColor: PROJECT_STATUS_COLORS[status as keyof typeof PROJECT_STATUS_COLORS],
                                    color: 'white !important',
                                    fontWeight: 600,
                                    mt: 0.5,
                                    '& .MuiChip-icon': { color: 'white' }
                                }}
                                size="small"
                            />
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                        }}
                        aria-label="Fermer"
                    >
                        <Close />
                    </IconButton>
                </Box>

                {/* ========== CONTENU SCROLLABLE ========== */}
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3,
                }}>
                    {globalError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {globalError}
                        </Alert>
                    )}

                    <Snackbar
                        open={globalInfo !== ''}
                        autoHideDuration={6000}
                        onClose={() => setGlobalInfo('')}
                        message={globalInfo}
                    />

                    {/* Status Management Section */}
                    <Card className="form-card" sx={{ mb: 2 }}>
                        <div className="form-header">
                            <h3 className="form-title">Statut du projet</h3>
                            <p className="form-subtitle">Modifiez librement le statut selon vos besoins</p>
                        </div>

                        <CardContent className="form-content">
                            <Select
                                value={status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                fullWidth
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }
                                }}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: PROJECT_STATUS_COLORS[selected as keyof typeof PROJECT_STATUS_COLORS],
                                            }}
                                        />
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {PROJECT_STATUS_LABELS[selected as keyof typeof PROJECT_STATUS_LABELS]}
                                        </Typography>
                                    </Box>
                                )}
                            >
                                {Object.values(PROJECT_STATUS).map((statusOption) => (
                                    <MenuItem
                                        key={statusOption}
                                        value={statusOption}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            py: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: PROJECT_STATUS_COLORS[statusOption as keyof typeof PROJECT_STATUS_COLORS],
                                            }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {PROJECT_STATUS_LABELS[statusOption as keyof typeof PROJECT_STATUS_LABELS]}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {PROJECT_STATUS_DESCRIPTIONS[statusOption as keyof typeof PROJECT_STATUS_DESCRIPTIONS]}
                                            </Typography>
                                        </Box>
                                        {status === statusOption && (
                                            <CheckCircle sx={{ color: PROJECT_STATUS_COLORS[statusOption as keyof typeof PROJECT_STATUS_COLORS], fontSize: 20 }} />
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>

                            {/* Description du statut actuel */}
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: `${PROJECT_STATUS_COLORS[status as keyof typeof PROJECT_STATUS_COLORS]}10`,
                                borderLeft: `4px solid ${PROJECT_STATUS_COLORS[status as keyof typeof PROJECT_STATUS_COLORS]}`,
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    {PROJECT_STATUS_DESCRIPTIONS[status as keyof typeof PROJECT_STATUS_DESCRIPTIONS]}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Form Card */}
                    <Card className="form-card">
                        <div className="form-header">
                            <h3 className="form-title">Détails du projet</h3>
                            <p className="form-subtitle">
                                {canEditFinancialFields()
                                    ? "Modifiez les informations du projet"
                                    : "Seuls le nom et la description sont modifiables après démarrage"
                                }
                            </p>
                        </div>

                        <CardContent className="form-content">
                            <div className="form-grid">
                                {/* Champs basiques - modifiables même après démarrage */}
                                <div className="form-row">
                                    <TextField
                                        label="Nom du projet"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        disabled={isSubmitting || !canEditBasicFields()}
                                        fullWidth
                                        className="form-field"
                                    />
                                </div>

                                <div className="form-row">
                                    <TextField
                                        label="Description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        error={!!formErrors.description}
                                        helperText={formErrors.description}
                                        disabled={isSubmitting || !canEditBasicFields()}
                                        multiline
                                        rows={3}
                                        fullWidth
                                        className="form-field"
                                    />
                                </div>

                                {/* Champs financiers - verrouillés après démarrage */}
                                <div className="form-row">
                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Contribution"
                                            type="number"
                                            value={contribution}
                                            onChange={(e) => setContribution(Number(e.target.value))}
                                            error={!!formErrors.contribution}
                                            helperText={formErrors.contribution}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            fullWidth
                                            className="form-field"
                                            InputProps={{
                                                endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <Select
                                            value={currency}
                                            onChange={handleCurrencyChange}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            className="form-field currency-select"
                                            sx={{ minWidth: 120 }}
                                        >
                                            {CURRENCY.map((curr) => (
                                                <MenuItem key={curr.label} value={curr.label}>
                                                    {curr.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Tooltip>
                                </div>

                                {/* Jours d'échéance et retrait - verrouillés après démarrage */}
                                <div className="form-row">
                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Jour d'échéance"
                                            type="number"
                                            value={dueDay}
                                            onChange={(e) => setDueDay(Number(e.target.value))}
                                            error={!!formErrors.dueDay}
                                            helperText={formErrors.dueDay}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            inputProps={{ min: 1, max: 31 }}
                                            fullWidth
                                            className="form-field"
                                            InputProps={{
                                                endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Jour de retrait"
                                            type="number"
                                            value={withdrawalDay}
                                            onChange={(e) => setWithdrawalDay(Number(e.target.value))}
                                            error={!!formErrors.withdrawalDay}
                                            helperText={formErrors.withdrawalDay}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            inputProps={{ min: 1, max: 31 }}
                                            fullWidth
                                            className="form-field"
                                            InputProps={{
                                                endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                            }}
                                        />
                                    </Tooltip>
                                </div>

                                {/* Pénalités et fond de caisse - verrouillés après démarrage */}
                                <div className="form-row">
                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label="Pénalité par jour (montant fixe)"
                                            type="number"
                                            value={penaltyAmountPerDay}
                                            onChange={(e) => setPenaltyAmountPerDay(Number(e.target.value))}
                                            error={!!formErrors.penaltyAmountPerDay}
                                            helperText={formErrors.penaltyAmountPerDay}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            fullWidth
                                            className="form-field"
                                            InputProps={{
                                                endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                            }}
                                        />
                                    </Tooltip>

                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <TextField
                                            label={`Fond de caisse (${CURRENCY.find(c => c.label === currency)?.symbol || '€'})`}
                                            type="number"
                                            value={cashFundAmount}
                                            onChange={(e) => setCashFundAmount(Number(e.target.value))}
                                            error={!!formErrors.cashFundAmount}
                                            helperText={formErrors.cashFundAmount || "Montant fixe réservé"}
                                            disabled={isSubmitting || !canEditFinancialFields()}
                                            inputProps={{ min: 0, max: 1000000, step: 0.01 }}
                                            fullWidth
                                            className="form-field"
                                            InputProps={{
                                                endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                            }}
                                        />
                                    </Tooltip>
                                </div>

                                {/* Dates - verrouillées après démarrage */}
                                <div className="form-row">
                                    <Tooltip
                                        title={!canEditFinancialFields() ? lockedFieldTooltip : ""}
                                        arrow
                                        placement="top"
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <DatePicker
                                                label="Date de début"
                                                value={startDate}
                                                onChange={(newValue) => setStartDate(newValue)}
                                                disabled={isSubmitting || !canEditFinancialFields()}
                                                slotProps={{
                                                    textField: {
                                                        error: !!formErrors.startDate,
                                                        helperText: formErrors.startDate,
                                                        fullWidth: true,
                                                        className: "form-field",
                                                        InputProps: {
                                                            endAdornment: !canEditFinancialFields() ? <Lock fontSize="small" color="disabled" /> : null
                                                        }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>

                                    <DatePicker
                                        label="Date de fin (calculée automatiquement)"
                                        value={endDate}
                                        disabled={true}
                                        slotProps={{
                                            textField: {
                                                helperText: "Calculée selon la somme des parts des participants",
                                                fullWidth: true,
                                                className: "form-field"
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                        </CardContent>
                    </Card>
                </Box>

                {/* ========== FOOTER FIXE ========== */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                    p: 2.5,
                    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                    background: 'rgba(248, 249, 250, 0.95)',
                    flexShrink: 0,
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                        }}
                    >
                        Annuler
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                        startIcon={<Save />}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            background: 'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-disabled': {
                                background: 'rgba(0, 0, 0, 0.12)',
                            }
                        }}
                    >
                        {isSubmitting ? 'Modification...' : 'Modifier le projet'}
                    </Button>
                </Box>
            </Box>
        </Modal>

        {/* Dialog de confirmation pour les modifications non sauvegardées */}
        <Dialog
            open={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
        >
            <DialogTitle id="confirm-dialog-title">
                Modifications non sauvegardées
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="confirm-dialog-description">
                    Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter sans enregistrer ?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={() => setShowConfirmDialog(false)}
                    variant="outlined"
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    Continuer l'édition
                </Button>
                <Button
                    onClick={forceClose}
                    color="error"
                    variant="contained"
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(45deg, #f5576c, #f093fb)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #e04558, #e080e8)',
                        }
                    }}
                >
                    Quitter sans sauvegarder
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
